
import { useQuery as useReactQuery, UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { useErrorHandler } from '@/hooks/useErrorHandler';
import { ErrorCategory } from '@/utils/errorHandler';
import { useMemo } from 'react';

interface UseQueryProps<TData, TError = Error> {
  queryKey: readonly unknown[];
  queryFn: () => Promise<TData>;
  options?: Omit<UseQueryOptions<TData, TError, TData>, 'queryKey' | 'queryFn'>;
  errorHandlingOptions?: {
    componentName: string;
    operationName: string;
    showToast?: boolean;
    category?: ErrorCategory;
    additionalData?: Record<string, unknown>;
  };
}

/**
 * Enhanced query hook with standardized error handling
 * and consistent patterns for data fetching
 */
export function useQuery<TData, TError = Error>({
  queryKey,
  queryFn,
  options,
  errorHandlingOptions
}: UseQueryProps<TData, TError>): UseQueryResult<TData, TError> {
  // Get the error handler for this component
  const errorHandler = useErrorHandler(
    errorHandlingOptions?.componentName || 'useQuery'
  );

  // Memoize queryFn to wrap it with error handling
  const enhancedQueryFn = useMemo(() => {
    return async () => {
      try {
        return await queryFn();
      } catch (error) {
        const category = errorHandlingOptions?.category || ErrorCategory.DATABASE;
        const operationName = errorHandlingOptions?.operationName || 'query';
        const showToast = errorHandlingOptions?.showToast ?? false;
        
        // Use our centralized error handler
        throw errorHandler.handleError(
          error,
          operationName,
          { 
            ...errorHandlingOptions?.additionalData,
            category 
          },
          showToast
        );
      }
    };
  }, [queryFn, errorHandler, errorHandlingOptions]);
  
  // Create meta object with proper handling for onError callback
  const metaObj = options?.meta || {};

  // Use the standard React Query hook with our enhanced error handling
  return useReactQuery({
    queryKey,
    queryFn: enhancedQueryFn,
    ...options,
    meta: {
      ...metaObj,
      onError: (error: TError) => {
        // Also handle errors in the standard way
        if (errorHandlingOptions?.showToast) {
          errorHandler.handleError(
            error,
            errorHandlingOptions.operationName || 'query failed',
            errorHandlingOptions.additionalData || {},
            true
          );
        }
        
        // Call the original onError if provided and it's a function
        if (metaObj.onError && typeof metaObj.onError === 'function') {
          metaObj.onError(error);
        }
      }
    }
  });
}
